import classNames from 'classnames'
import { Fragment, MouseEvent, useId, useState } from 'react'

import { displayedText } from '../../model/helpers'
import {
  IScreeningCompareRow,
  IScreeningMatchedEntry,
  IScreeningMatchView,
  IScreeningRerunEntry,
} from '../../model/types'
import {
  criterionText,
  COMPETITOR_BAD_ACTOR_REVIEW_TEXT as TEXT,
  useReviewText,
} from '../translations'

import { ScreeningCompareTable } from './ScreeningCompareTable'

import styles from './ScreeningMatchesList.module.scss'

type TScreeningMatchesListProps = {
  caption: string
  regionLabel: string
  oneOpenAtATime?: true
} & (
  | { comparison: 'stored'; entries: readonly IScreeningMatchedEntry[] }
  | { comparison: 'complete'; entries: readonly IScreeningRerunEntry[] }
)

interface IListedMatch {
  key: string
  match: IScreeningMatchView
  compare: readonly IScreeningCompareRow[] | undefined
  alternativeNames: readonly string[]
}

const entryKeyOf = (match: IScreeningMatchView, index: number): string =>
  `${index}:${String(match.listedName)}`

const nameKeyOf = (name: unknown): string =>
  typeof name === 'string' ? name.trim().toLowerCase() : ''

const distinctAlternativeNames = (
  listedNames: readonly unknown[],
  alternativeNames: readonly unknown[]
): string[] => {
  const seen = new Set(['', ...listedNames.map(nameKeyOf)])

  return alternativeNames.filter((name): name is string => {
    const key = nameKeyOf(name)

    if (seen.has(key)) {
      return false
    }

    seen.add(key)

    return true
  })
}

const listedMatchesOf = (props: TScreeningMatchesListProps): IListedMatch[] =>
  props.comparison === 'stored'
    ? props.entries.map((entry, index) => ({
        key: entryKeyOf(entry, index),
        match: entry,
        compare: entry.compare,
        alternativeNames: [],
      }))
    : props.entries.map((entry, index) => ({
        key: entryKeyOf(entry, index),
        match: entry,
        compare: entry.compare,
        alternativeNames: distinctAlternativeNames(
          [entry.listedName, entry.listed.listedName],
          entry.listed.alternativeNames
        ),
      }))

export const ScreeningMatchesList = (props: TScreeningMatchesListProps) => {
  const { comparison, caption, regionLabel, oneOpenAtATime } = props
  const text = useReviewText()
  const noValue = text(TEXT.noValue)
  const panelIdPrefix = useId()
  const [openEntries, setOpenEntries] = useState<ReadonlySet<string>>(new Set())
  const listedMatches = listedMatchesOf(props)
  const shownCaption = listedMatches.some(({ compare }) => compare !== undefined)
    ? `${caption} ${text(TEXT.compareInstruction)}`
    : caption

  const toggle = (key: string) =>
    setOpenEntries((previous) => {
      if (previous.has(key)) {
        const next = new Set(previous)
        next.delete(key)

        return next
      }

      return new Set(oneOpenAtATime ? [key] : [...previous, key])
    })

  const toggleFromRow = (event: MouseEvent<HTMLTableRowElement>, key: string) => {
    const fromToggleButton =
      event.target instanceof Element && event.target.closest('button') !== null
    const selectingText = (window.getSelection()?.toString() ?? '') !== ''

    if (!fromToggleButton && !selectingText) {
      toggle(key)
    }
  }

  const howItMatchedOf = (match: IScreeningMatchView) => {
    const label = criterionText(match.criterion)

    return displayedText(label === null ? match.criterion : text(label), noValue)
  }

  const scoreOf = (match: IScreeningMatchView) => {
    if (match.score === null) {
      return noValue
    }

    const score = text(TEXT.scoreValue, { score: match.score })

    return match.band === 'high' ? `${score}, ${text(TEXT.scoreHigh)}` : score
  }

  return (
    <div role="region" aria-label={regionLabel} tabIndex={0} className={styles.scroll}>
      <table className={styles.table}>
        <caption>{shownCaption}</caption>
        <thead>
          <tr>
            <th scope="col">{text(TEXT.colListedName)}</th>
            <th scope="col">{text(TEXT.colHow)}</th>
            <th scope="col">{text(TEXT.colScore)}</th>
          </tr>
        </thead>
        <tbody>
          {listedMatches.map(({ key, match, compare, alternativeNames }, index) => {
            const listedName = displayedText(match.listedName, noValue)
            const open = openEntries.has(key)
            const panelId = `${panelIdPrefix}-${index}`

            return (
              <Fragment key={key}>
                <tr
                  className={classNames(styles.entryRow, {
                    [styles.expandableRow]: compare !== undefined,
                    [styles.openRow]: open,
                  })}
                  onClick={compare === undefined ? undefined : (event) => toggleFromRow(event, key)}
                >
                  <th scope="row">
                    <span className={styles.listedName}>
                      {compare === undefined ? (
                        listedName
                      ) : (
                        <button
                          type="button"
                          className={styles.toggle}
                          aria-expanded={open}
                          aria-controls={open ? panelId : undefined}
                          onClick={() => toggle(key)}
                        >
                          <span aria-hidden="true" className={styles.chevron} />
                          {listedName}
                        </button>
                      )}
                      {match.sourceRows > 1 && (
                        <span>{` (${text(TEXT.listedTimes, { count: match.sourceRows })})`}</span>
                      )}
                    </span>
                  </th>
                  <td>{howItMatchedOf(match)}</td>
                  <td>{scoreOf(match)}</td>
                </tr>
                {open && compare !== undefined && (
                  <tr className={styles.panelRow}>
                    <td colSpan={3} id={panelId}>
                      <div className={styles.panel}>
                        {alternativeNames.length > 0 && (
                          <p>
                            {text(TEXT.compareAlsoListedAs, {
                              names: alternativeNames.join(', '),
                            })}
                          </p>
                        )}
                        <ScreeningCompareTable
                          accessibleName={text(TEXT.compareLabel, { listedName })}
                          rows={compare}
                        />
                        {comparison === 'stored' && <p>{text(TEXT.compareStoredNote)}</p>}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
